import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/button'
import { Skeleton } from '@/components/skeleton'
import type { Publication, Publications } from '@/models/publications'
import api from '@/services/api'

import { Card } from './card'
import { LabelPrimary } from './label-primary'
import { OthersList } from './others-list'

export function Publications() {
  const [letters, setLetters] = useState<Publications | null>(null)
  const [news, setNews] = useState<Publications | null>(null)
  const [highlighted, setHighlighted] = useState<Publication | null>(null)

  const getPublicationOfTheTypeLetters = useCallback(() => {
    async function fetchLetters() {
      await api
        .get<Publications>('/publication?limite=6&tipo=L')
        .then((resp) => {
          setLetters(resp.data)
        })
        .catch((err) => {
          toast.error(err.response.data.message)
        })
    }
    fetchLetters()
  }, [])

  const getPublicationOfTheTypeNews = useCallback(() => {
    async function fetchNews() {
      await api
        .get<Publications>('/publication?limite=6&tipo=N')
        .then((resp) => {
          setNews(resp.data)
        })
        .catch((err) => {
          toast.error(err.response.data.message)
        })
    }
    fetchNews()
  }, [])

  const getPublicationHighlighted = useCallback(() => {
    async function fetchHighlighted() {
      await api
        .get<Publication>('/publication/highlighted')
        .then((resp) => {
          setHighlighted(resp.data)
        })
        .catch((err) => {
          toast.error(err.response.data.message)
        })
    }
    fetchHighlighted()
  }, [])

  useEffect(() => {
    Promise.all([
      getPublicationOfTheTypeLetters(),
      getPublicationOfTheTypeNews(),
      getPublicationHighlighted(),
    ])
  }, [
    getPublicationHighlighted,
    getPublicationOfTheTypeLetters,
    getPublicationOfTheTypeNews,
  ])

  return (
    <div className="content-container flex-1">
      <div className="space-y-10 2xl:space-y-20">
        <div className="bg-bios-green-3000 grid h-full rounded-2xl xl:min-h-125 xl:grid-cols-2 2xl:min-h-150">
          <div className="h-40 w-full overflow-hidden select-none lg:h-full">
            {highlighted && (
              <img
                src={highlighted.imagem}
                alt="publicações destaque"
                className="h-full w-full rounded-t-2xl object-cover lg:rounded-r-none lg:rounded-bl-2xl"
              />
            )}

            {!highlighted && (
              <Skeleton className="h-full rounded-l-2xl rounded-r-none" />
            )}
          </div>
          <div className="flex flex-col gap-5 px-8 py-10 xl:px-12 2xl:px-24 2xl:py-20">
            {highlighted && (
              <div className="flex flex-row items-center justify-center lg:justify-start">
                <h1 className="card-title text-shadow-title text-center text-2xl text-white lg:text-start 2xl:text-4xl">
                  {highlighted.titulo}
                </h1>
              </div>
            )}

            {!highlighted && (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 rounded-none" />
              </div>
            )}

            {highlighted && (
              <div className="flex h-full items-center justify-center lg:justify-start">
                <p className="line-clamp-9 text-center text-white lg:text-start">
                  {highlighted.subtitulo}
                </p>
              </div>
            )}

            {!highlighted && (
              <div className="flex h-full flex-col gap-2">
                <Skeleton className="h-4 rounded-none" />
                <Skeleton className="h-4 rounded-none" />
                <Skeleton className="mx-auto h-4 w-1/3 rounded-none lg:mx-0" />
              </div>
            )}

            {highlighted && (
              <div className="flex flex-row justify-center lg:justify-start">
                <Button
                  variant="outline"
                  className="border-bios-primary border bg-transparent text-white uppercase"
                  asChild
                >
                  <Link to={`/publication/${highlighted.id_publicacao}`}>
                    Saiba mais
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-y-6 lg:grid-cols-2 lg:gap-x-24">
          <div className="bg-monochromatic-200 py-5">
            <LabelPrimary variant="news" title="Notícias" />
            <div className="flex flex-col gap-6">
              {news &&
                news.publications?.slice(0, 3).map((publication) => (
                  <Link
                    key={publication.id_publicacao}
                    to={`/publication/${publication.id_publicacao}`}
                  >
                    <Card
                      variant="news"
                      img={publication.imagem}
                      subTitle={publication.subtitulo}
                      title={publication.titulo}
                    />
                  </Link>
                ))}
              {!news && (
                <>
                  <Skeleton className="h-32" />
                  <Skeleton className="h-32" />
                  <Skeleton className="h-32" />
                </>
              )}
            </div>
          </div>

          <div className="bg-monochromatic-200 py-5">
            <LabelPrimary variant="letters" title="6Bios letters" />
            <div className="flex flex-col gap-6">
              {letters &&
                letters.publications?.slice(0, 3).map((publication) => (
                  <Link
                    key={publication.id_publicacao}
                    to={`/publication/${publication.id_publicacao}`}
                  >
                    <Card
                      variant="letters"
                      img={publication.imagem}
                      subTitle={publication.subtitulo}
                      title={publication.titulo}
                    />
                  </Link>
                ))}
              {!letters && (
                <>
                  <Skeleton className="h-32" />
                  <Skeleton className="h-32" />
                  <Skeleton className="h-32" />
                </>
              )}
            </div>
          </div>

          <div className="lg:px-6">
            {news && news.publications?.length > 3 && (
              <OthersList
                variant="news"
                data={news.publications?.slice(3, 6)}
                showMore
                title="Outras notícias"
              />
            )}
          </div>

          <div className="lg:px-6">
            {letters && letters.publications?.length > 3 && (
              <OthersList
                variant="letters"
                data={letters.publications?.slice(3, 6)}
                showMore
                title="Outras letters"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
